import {
  AddTask,
  Block,
  Check,
  DescriptionOutlined,
  LocalPrintshopOutlined,
  Restaurant,
} from '@mui/icons-material';
import { Button, Stack, ToggleButton, useTheme } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import { useToggle } from 'react-use';

import type { OrderModel, OrderStatusModel } from '@merchant-clients/order';

import { AppBottomActionBar, useToast } from '@frontend/components';
import { useUpdateOrderMutation } from '@frontend/data';
import { tOrder } from '@frontend/locales';
import { usePrinterContext } from '@frontend/utils';

import { orderNextStatusMap } from './order-status-transition.utils';

const orderPrimaryActionStatusMap = new Map([
  ['PENDING', tOrder.actions.confirm],
  ['CONFIRMED', tOrder.actions.ready],
  ['READY', tOrder.actions.close],
  ['CLOSED', tOrder.actions.close],
  ['REJECTED', tOrder.actions.close],
]);

export interface OrderActionbarProps {
  orders: OrderModel[];
  toggleDetails(state: boolean): void;
}

export const OrderActionBar = (props: OrderActionbarProps) => {
  const { shopId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderIdParam = searchParams.get('orderId');
  const theme = useTheme();
  const { showToast } = useToast();
  const orderStatusMutation = useUpdateOrderMutation();
  const [showDetails, setShowDetails] = useToggle(false);
  const selectedOrder = props.orders.find((order) => order.id === orderIdParam);

  const { printingReady, printMerchantOrderAction } = usePrinterContext();

  // The details toggle only renders with a selected order, so close it once that
  // order is gone (deselected or no longer in the list).
  if (!selectedOrder && showDetails) {
    setShowDetails(false);
  }

  const primaryActionIcon = (status: OrderStatusModel) => {
    switch (status) {
      case 'PENDING':
        return <AddTask color="inherit" />;
      case 'CONFIRMED':
        return <Restaurant color="inherit" />;
      case 'READY':
      case 'CLOSED':
      case 'REJECTED':
      case 'REFUNDED':
        return <Check color="inherit" />;
    }
  };

  const updateStatus = () => {
    if (!selectedOrder || !orderNextStatusMap.has(selectedOrder.status)) {
      return;
    }

    const nextStatus = orderNextStatusMap.get(selectedOrder.status)!;

    orderStatusMutation.mutate(
      {
        shopId: shopId!,
        orderId: selectedOrder.id,
        status: nextStatus,
      },
      {
        onSuccess: () => {
          searchParams.delete('orderId');
          setSearchParams(searchParams, { replace: true });
        },
      },
    );
  };

  const rejectOrder = () => {
    if (
      !selectedOrder ||
      selectedOrder.status === 'REJECTED' ||
      selectedOrder.status === 'CLOSED'
    ) {
      return;
    }

    orderStatusMutation.mutate(
      {
        shopId: shopId!,
        orderId: selectedOrder.id,
        status: 'REJECTED',
      },
      {
        onSuccess: () => {
          showToast({
            header: tOrder.actions.orderRejected(),
            body: tOrder.actions.orderRejectedDescription(),
            severity: 'success',
          });
          searchParams.delete('orderId');
          setSearchParams(searchParams, { replace: true });
        },
      },
    );
  };

  return (
    <AppBottomActionBar slideEnabled slideCondition={!!orderIdParam}>
      {selectedOrder && (
        <Stack
          sx={{
            height: '112px',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            size="large"
            fullWidth
            color="error"
            data-eventid="order-action-reject"
            disabled={selectedOrder.status !== 'PENDING'}
            onClick={rejectOrder}
            sx={{
              height: '100%',
              alignItems: 'center',
              color: theme.palette.error.dark,
              borderColor: theme.palette.divider,
            }}
            startIcon={<Block color="inherit" />}
          >
            {tOrder.actions.reject()}
          </Button>

          {printingReady && (
            <Button
              variant="outlined"
              size="large"
              fullWidth
              data-eventid="order-action-print"
              sx={{
                height: '100%',
                alignItems: 'center',
                borderColor: theme.palette.divider,
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              loading={printMerchantOrderAction.isRunning}
              onClick={() => printMerchantOrderAction.run(selectedOrder)}
              startIcon={<LocalPrintshopOutlined color="inherit" />}
            >
              {tOrder.actions.print()}
            </Button>
          )}

          <ToggleButton
            size="large"
            fullWidth
            data-eventid="order-action-details"
            onChange={() => {
              setShowDetails();
              props.toggleDetails(!showDetails);
            }}
            selected={showDetails}
            value="details"
            sx={{
              height: '100%',
              alignItems: 'center',
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              '&.Mui-selected': {
                color: theme.palette.text.secondary,
              },
            }}
          >
            <DescriptionOutlined color="inherit" sx={{ mr: 1 }} />
            {!showDetails
              ? tOrder.actions.viewDetails()
              : tOrder.actions.hideDetails()}
          </ToggleButton>

          <Button
            variant="contained"
            color="success"
            size="large"
            fullWidth
            data-eventid="order-action-primary"
            disabled={
              selectedOrder.status === 'CLOSED' ||
              selectedOrder.status === 'REJECTED' ||
              selectedOrder.loadingState
            }
            startIcon={primaryActionIcon(selectedOrder.status)}
            onClick={updateStatus}
            sx={{
              height: '100%',
              alignItems: 'center',
              backgroundColor: theme.palette.success.main,
              borderColor: theme.palette.success.dark,
              color: theme.palette.common.white,
              '&:hover': {
                backgroundColor: theme.palette.success.dark,
              },
            }}
          >
            {orderPrimaryActionStatusMap.get(selectedOrder.status)!()}
          </Button>
        </Stack>
      )}
    </AppBottomActionBar>
  );
};
